import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { keyBy, reverse } from 'lodash'
import { mixedSort } from '@app/utils/sort'
import { InsightsStaticColumns, RIGHT_ALIGN_FILTER_TYPES } from '../constants'
import { filterDefinitions } from '../filter-definitions'
import { useInsightsRootStore } from './insights-root'
import { useFiltersStore } from './filters'
import I18n from '@app/adapters/I18n'
import type { ActiveFilter } from './filters/types'

const ASC = 'asc'
const DESC = 'desc'

// filterDefinitions is a static read-only array; keyBy once at module level
// to avoid reconstructing the lookup map on every getColumnSortMethod call.
const filterDefinitionsByType = keyBy(filterDefinitions, 'type')

interface Column {
  name: string
  title: string
  subtitle?: string
  classes?: string
  alignment?: string
  filter?: unknown
}

interface Filter {
  uuid: string
  type: string
  settings?: Record<string, unknown>
  [key: string]: unknown
}

export const useColumnsStore = defineStore('insights-columns', () => {
  const sortColumn = ref('name')
  const sortType = ref(ASC)
  const filtersStore = useFiltersStore()

  const isAscending = computed(() => sortType.value === ASC)

  const defaultColumns = computed((): Column[] => {
    const columns: Column[] = [
      {
        name: InsightsStaticColumns.NAME,
        title: I18n.t('companies_overview.columns.client_file_name'),
        classes: 'sf-fwb',
      },
    ]
    if (useInsightsRootStore().periodSelection) {
      columns.push({
        name: InsightsStaticColumns.SELECTED_PERIOD,
        title: I18n.t('companies_overview.selected_period_title'),
      })
    }
    return columns
  })

  const additionalColumns = computed((): Column[] => {
    const activeFilters = filtersStore.displayedFilters as Filter[]

    return activeFilters.map((filter) => {
      let alignment = ''

      if (
        RIGHT_ALIGN_FILTER_TYPES.includes(
          filter.type as 'Formula' | 'ReconciliationResult'
        )
      ) {
        alignment = 'sf-align-r tw-ml-auto'
      }

      return {
        name: filter.uuid,
        title: filtersStore.getFilterHeaderTitle(filter as ActiveFilter),
        subtitle: filtersStore.getFilterHeaderSubtitle(filter as ActiveFilter),
        alignment,
        filter,
      }
    })
  })

  const companyColumns = computed((): Column[] => [
    ...defaultColumns.value,
    ...additionalColumns.value,
  ])

  function getColumnSortMethod(companies: Record<number, unknown>): unknown[] {
    const activeFilters = filtersStore.displayedFilters as Filter[]
    const filtersByUuid = keyBy(activeFilters, 'uuid')

    type CompanyRow = Record<string, unknown>

    const accessorMethod = (o: CompanyRow): unknown => {
      const filter = filtersByUuid[sortColumn.value]
      const filterType = filter?.type
      const filterValues = o.filter_values as Record<
        string,
        {
          formatted_value?: unknown
          sort_value?: unknown
          filter_error?: unknown
        }
      >
      const filterValue = filterValues?.[sortColumn.value]

      if (filterType) {
        const filterDefinition = filterDefinitionsByType[filterType]
        const def = filterDefinition as {
          sortValue?: (v: unknown) => unknown
        } | null
        if (def?.sortValue) {
          return def.sortValue(filterValue?.formatted_value)
        }
      }

      return filterValue?.sort_value
    }

    const priorityMethod = (o: CompanyRow): boolean => {
      const filterValues = o.filter_values as Record<
        string,
        { filter_error?: unknown }
      >
      return !!filterValues?.[sortColumn.value]?.filter_error
    }

    const items = Object.values(companies) as CompanyRow[]
    const sorted = mixedSort<CompanyRow>(
      items,
      accessorMethod as <O>(o: O) => O,
      priorityMethod as <O>(o: O) => boolean
    )
    if (sortType.value !== ASC) {
      return reverse(sorted)
    }
    return sorted
  }

  function changeSort(column: string): void {
    if (sortColumn.value === column) {
      sortType.value = sortType.value === ASC ? DESC : ASC
    } else {
      sortColumn.value = column
      sortType.value = ASC
    }
  }

  function setSortColumn({
    sortColumn: col,
    sortType: type,
  }: {
    sortColumn: string
    sortType?: string
  }): void {
    sortColumn.value = col
    sortType.value = type ?? ASC
  }

  function setSortAndType({
    sortColumn: col,
    sortType: type,
  }: {
    sortColumn: string
    sortType: string
  }): void {
    sortColumn.value = col
    sortType.value = type
  }

  return {
    sortColumn,
    sortType,
    isAscending,
    defaultColumns,
    additionalColumns,
    companyColumns,
    getColumnSortMethod,
    changeSort,
    setSortColumn,
    setSortAndType,
  }
})
